// @ts-check
import {
  as_number,
  as_object,
  first_number,
  first_string,
  record_value
} from './tenant_configuration_editor_normalisers';
import { project_creator_id } from './tenant_configuration_editor_project_helpers';

/**
 * @typedef {import('./tenant_configuration_editor_types').RoleOption} RoleOption
 * @typedef {import('./tenant_configuration_editor_types').ProjectAssignment} ProjectAssignment
 * @typedef {import('./tenant_configuration_editor_types').UserOption} UserOption
 * @typedef {import('./tenant_configuration_editor_types').TenantProject} TenantProject
 */

/** @param {unknown} value @returns {RoleOption|null} */
export function normalize_role_option(value) {
  const role = as_object(value);
  const key = first_number([record_value(role, 'id'), record_value(role, 'role_id')]);
  if (!key) return null;
  return role_option_payload(role, key);
}

function role_option_payload(role, key) {
  const option = Object.fromEntries([]);
  option.key = key;
  option.title = first_string([record_value(role, 'role'), record_value(role, 'name')]);
  option.description = first_string([record_value(role, 'description')]) || 'Tenant access role';
  return option;
}

/** @param {RoleOption|null} value @returns {value is RoleOption} */
export function is_role_option(value) {
  return value !== null;
}

/** @param {unknown} value @returns {ProjectAssignment} */
export function normalize_project_assignment(value) {
  const assignment = Object.fromEntries([]);
  assignment.project = as_number(record_value(as_object(value), 'project'));
  return assignment;
}

/** @param {unknown} value @returns {UserOption} */
export function normalize_user_option(value) {
  const profile = as_object(value);
  const user_value = record_value(profile, 'user') || profile;
  const user = as_object(user_value);
  const role_value = record_value(user, 'role') || record_value(profile, 'role');
  const role = as_object(role_value);
  const option = Object.fromEntries([]);
  option.id = first_number([
      record_value(profile, 'user_id'),
      record_value(user, 'id'),
      record_value(profile, 'id')
    ]);
  option.first_name = first_string([record_value(user, 'first_name'), record_value(profile, 'first_name')]);
  option.last_name = first_string([record_value(user, 'last_name'), record_value(profile, 'last_name')]);
  option.email = first_string([record_value(user, 'email'), record_value(profile, 'email')]);
  option.phone = first_string([
      record_value(profile, 'phone_number'),
      record_value(as_object(record_value(profile, 'profile')), 'phone_number'),
      record_value(user, 'phone_number')
    ]);
  option.role_id = first_number([
      record_value(role, 'id'),
      record_value(user, 'role_id'),
      record_value(profile, 'role_id')
    ]);
  option.role_name = first_string([
      record_value(role, 'role'),
      record_value(role, 'name'),
      role_value
    ]);
  return option;
}

/**
 * @param {TenantProject|null} project
 * @param {UserOption[]} users
 * @returns {UserOption|null}
 */
export function selected_tenant_contact(project, users = []) {
  const creator_id = project_creator_id(project);
  return users.find((user) => user.role_name === 'System Admin' && user.id !== creator_id)
    || users.find((user) => user.role_name === 'System Admin')
    || users[0]
    || null;
}
